/*
 * Hardway Performance — Order Form Keeper
 *
 * Keeps the answers a customer has typed into a product's order fields
 * (the tuning forms: VIN, serial numbers, sensor selections, build notes…)
 * so they are still there if the page is reloaded for any reason — an app
 * refreshing the page, a manual refresh, or a phone reloading the tab after
 * the customer leaves to look up their VIN.
 *
 * - Answers are kept in sessionStorage: this browser tab only, never sent
 *   anywhere, gone when the tab is closed.
 * - Acknowledgment checkboxes are NOT restored — the customer must tick
 *   those again on purpose.
 * - Saved answers are cleared once the product is added to the cart.
 *
 * Loaded with `defer` from layout/theme.liquid on product pages, so it runs
 * after the form is in the page but before the other product scripts
 * initialise (they then read the restored values).
 */
(function () {
  'use strict';

  var FIELD_SELECTOR = 'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]';
  var SKIP_TYPES = { hidden: 1, file: 1, checkbox: 1, submit: 1, button: 1, password: 1 };
  var KEY = 'hwFormKeeper:' + window.location.pathname;
  var saveTimer = null;
  var dirty = false;

  var store;
  try {
    store = window.sessionStorage;
    store.getItem(KEY);
  } catch (e) {
    return; // storage blocked (private mode etc.) — nothing to do
  }

  function isKept(el) {
    if (!el || !el.name) return false;
    if (el.name.indexOf('properties[_') === 0) return false; // internal/hidden properties
    var type = (el.type || '').toLowerCase();
    return !SKIP_TYPES[type];
  }

  function fields() {
    return Array.prototype.filter.call(document.querySelectorAll(FIELD_SELECTOR), isKept);
  }

  function read() {
    try { return JSON.parse(store.getItem(KEY) || '{}') || {}; } catch (e) { return {}; }
  }

  function save() {
    dirty = false;
    var data = {};
    var any = false;
    fields().forEach(function (el) {
      var type = (el.type || '').toLowerCase();
      if (type === 'radio') {
        if (el.checked) { data[el.name] = el.value; any = true; }
        return;
      }
      if (el.value !== '') { data[el.name] = el.value; any = true; }
    });
    try {
      if (any) store.setItem(KEY, JSON.stringify(data));
      else store.removeItem(KEY);
    } catch (e) { /* storage full or blocked — ignore */ }
  }

  function scheduleSave() {
    dirty = true;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(save, 150);
  }

  function fire(el) {
    try {
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (e) { /* very old browser — skip */ }
  }

  function restore() {
    var data = read();
    var names = Object.keys(data);
    if (!names.length) return;

    var changed = [];
    fields().forEach(function (el) {
      if (!Object.prototype.hasOwnProperty.call(data, el.name)) return;
      var saved = data[el.name];
      var tag = el.tagName.toLowerCase();
      var type = (el.type || '').toLowerCase();

      if (type === 'radio') {
        if (el.value === saved && !el.checked) { el.checked = true; changed.push(el); }
        return;
      }
      if (el.value !== '') return; // never overwrite something already filled in

      if (tag === 'select') {
        var ok = Array.prototype.some.call(el.options, function (o) {
          return o.value === saved && !o.disabled;
        });
        if (!ok) return; // option no longer offered
      }
      el.value = saved;
      if (el.value === saved) changed.push(el);
    });

    // Let any show/hide logic that is already listening react to the values.
    changed.forEach(fire);
  }

  function clear() {
    dirty = false;
    clearTimeout(saveTimer);
    try { store.removeItem(KEY); } catch (e) {}
  }

  function start() {
    if (!fields().length) return;

    restore();

    document.addEventListener('input', function (e) {
      if (isKept(e.target) && e.target.matches(FIELD_SELECTOR)) scheduleSave();
    }, true);
    document.addEventListener('change', function (e) {
      if (isKept(e.target) && e.target.matches(FIELD_SELECTOR)) scheduleSave();
    }, true);
    window.addEventListener('pagehide', function () {
      if (dirty) save();
    });

    // Order is in the cart — the saved answers have done their job.
    document.addEventListener('ajaxProduct:added', clear);
  }

  start();
})();
